import { Link, Navigate, useParams } from 'react-router-dom';
import { BrandLogo, buttonVariants, cn } from '@examflow/ui';
import { buildExamPublicLandingModel } from '@examflow/learning-space';
import { BeianFooter } from '../components/BeianFooter';

const STAGE_BG =
  'radial-gradient(ellipse 70% 55% at 85% 20%, rgba(91,122,91,0.14), transparent 55%), radial-gradient(ellipse 50% 45% at 10% 80%, rgba(200,90,26,0.07), transparent 50%), #f7f8f6';

export default function ExamPublicLandingPage() {
  const { examId = '' } = useParams<{ examId: string }>();
  const model = buildExamPublicLandingModel(examId);

  if (!model) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="min-h-screen text-ink-primary" style={{ background: STAGE_BG }}>
      <div className="mx-auto w-full max-w-3xl px-6 sm:px-10 lg:px-12">
        <header className="relative z-20 flex h-16 items-center justify-between">
          <Link to="/" className="inline-flex items-center" aria-label="ExamFlow 首页">
            <BrandLogo variant="full" size={32} />
          </Link>
          <Link
            to={model.primaryAction.href ?? '/register'}
            className={cn(buttonVariants({ variant: 'primary' }), 'h-9 rounded-full px-5 text-sm')}
          >
            {model.primaryAction.label}
          </Link>
        </header>

        <section className="pb-10 pt-8">
          <p className="mb-4 text-[12px] font-semibold tracking-[0.04em] text-ink-muted">
            {model.eyebrow}
          </p>
          <h1 className="text-[36px] font-black leading-[1.15] tracking-tight sm:text-[44px]">
            <span className="block text-ink-primary">{model.title}</span>
            <span className="mt-1 block text-accent">{model.titleAccent}</span>
          </h1>
          <p className="mt-6 max-w-xl text-[15px] leading-7 text-ink-secondary sm:text-base sm:leading-8">
            {model.description}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to={model.primaryAction.href ?? '/register'}
              className={cn(
                buttonVariants({ variant: 'primary' }),
                'h-12 rounded-full px-8 text-[15px]'
              )}
            >
              {model.primaryAction.label}
            </Link>
            <Link
              to={model.secondaryAction.href ?? '/#exam-spaces'}
              className="text-sm font-semibold text-ink-secondary underline-offset-4 hover:text-ink-primary hover:underline"
            >
              {model.secondaryAction.label}
            </Link>
          </div>
        </section>

        <section className="border-t border-ink-primary/10 pb-12 pt-10" aria-labelledby="exam-structure">
          <h2 id="exam-structure" className="text-lg font-black tracking-tight">
            官方结构
          </h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-ink-secondary">
            {model.structureDisclaimer}
          </p>
          <ul className="mt-6 space-y-3">
            {model.skills.map((skill) => (
              <li
                key={skill.id}
                className="rounded-xl border border-ink-primary/8 bg-white px-4 py-3 shadow-sm"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold text-ink-primary">{skill.name}</span>
                  <span className="text-[11px] font-bold text-ink-muted">
                    {skill.includeInFullMock ? 'Full Mock' : '可选'}
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink-secondary">
                  {skill.durationMinutes} 分钟 · 官方 Mock {skill.officialMockQuestionCount} 题
                </p>
              </li>
            ))}
          </ul>
        </section>

        <footer className="pb-10">
          <BeianFooter className="px-0 pb-0" />
        </footer>
      </div>
    </main>
  );
}
